import React, { useState } from 'react'
import CenteredModal from '../../../../../../components/Modal/CenteredModal'
import Loader from '../../../../../../components/loader/Loader'
import { useQuery } from 'react-query'
import { api } from '../../../../../../api/courses'
import pcIcon from '../../../../../../assets/Bulk Upload.png'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

type PreviewModuleModalType = {
  openModal: boolean
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>
  modulePath: string,
  previewId: string,
  moduleId: string,
  isMulti: boolean,
  previewModuleMuation: any
}

type resultType = null | {
  score: number,
  status: string,
  completionStatus?: string,
  totalQuestions?: number,
  correctAnswers?: number,
}

type symbolType = 'en' | 'fr' | 'pt'

const languages: { name: string, symbol: symbolType }[] = [
  { name: 'English', symbol: 'en' },
  { name: 'French', symbol: 'fr' },
  { name: 'Portuguese', symbol: 'pt' },
]

const PreviewModuleModal: React.FC<PreviewModuleModalType> = (props): React.ReactElement => {

  const { openModal, setOpenModal, modulePath, isMulti } = props
  const [currentView, setCurrentView] = useState<'preview' | 'result'>('preview')
  const [language, setLanguage] = useState<symbolType>('en')
  const [fetchResult, setFetchResult] = useState<boolean>(false)
  const navigate = useNavigate()

  // GET request — second preview (result) — only fires when user clicks "Finish, View Result"
  const {
    data: resultData,
    isLoading: resultLoading,
    isError: resultError,
  } = useQuery(
    ['module-result', props.moduleId, props.previewId],
    () => api.getModuleResult({ id: props.moduleId, previewId: props.previewId, search: undefined }),
    {
      enabled: fetchResult && Boolean(props.moduleId) && Boolean(props.previewId),
      onError: () => toast.error('Failed to fetch module result.'),
    }
  )

  const result: resultType = resultData?.data?.previewData || null

  const handleLanguageChange = (newLang: symbolType) => {
    setLanguage(newLang)
    // POST — first preview, triggered when language changes
    props.previewModuleMuation.mutate({ id: props.moduleId, language: newLang })
  }

  const handleViewResult = () => {
    setCurrentView('result')
    setFetchResult(true)
  }

  const handleClose = () => {
    setOpenModal(false)
    setCurrentView('preview')
    setFetchResult(false)
  }

  return (
    <>
      <CenteredModal
        title={
          <div className='flex items-center gap-4'>
            <h5 className='text-md font-semibold flex items-center gap-1'>
              <span className='bi bi-eye'></span>
              {currentView === 'preview' ? 'Preview Module' : 'Module Result'}
            </h5>

            {/* Language selector — only shown on the preview view for multi-language modules */}
            {isMulti && currentView === 'preview' && (
              <select
                className='input-style bg-primaryGray text-xs py-1 ml-2'
                value={language}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleLanguageChange(e.target.value as symbolType)
                }
              >
                {languages.map((lang) => (
                  <option key={lang.symbol} value={lang.symbol}>
                    {lang.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        }
        bgColor={'bg-white'}
        open={openModal}
        setOpen={setOpenModal}
        style={{ padding: 0 }}
        width={'w-[800px] h-[540px] overflow-y-auto'}
        headerClassName={'flex justify-between px-3 pt-0 sticky'}
        withCloseBtn={false}
        closeButtonLabel={'Close'}
        btnBgColor={'bg-secondary'}
        onClose={null}
      >
        {/* View area */}
        <section className='text-left my-3 border-2 border-slate-300 border-double w-full h-[390px] rounded-lg overflow-hidden'>
          {currentView === 'preview' ? (
            // ── Preview View — POST result (iframe) ──────────────────────
            props.previewModuleMuation.isLoading ? (
              <div className='h-full flex items-center justify-center'>
                <Loader />
              </div>
            ) : modulePath ? (
              <iframe
                src={modulePath}
                className='w-full h-full p-0 m-0 border-0'
                title='Module Preview'
              />
            ) : (
              <div className='h-full flex items-center justify-center text-sm text-gray-400'>
                No preview available.
              </div>
            )
          ) : (
            // ── Result View — GET result ──────────────────────────────────
            resultLoading ? (
              <div className='h-full flex items-center justify-center'>
                <Loader />
              </div>
            ) : resultError ? (
              <div className='h-full flex items-center justify-center text-sm text-red-400'>
                Failed to load result. Please try again.
              </div>
            ) : (
              <div className='h-full w-full flex flex-col items-center justify-center gap-4 px-6'>
                <img src={pcIcon} alt='result icon' className='w-[200px] h-[130px] object-contain' />

                <div className='w-full max-w-[360px] space-y-3'>
                  {/* Score */}
                  <div className='flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2'>
                    <span className='text-sm text-gray-600 font-medium'>Score</span>
                    <span className='text-sm font-semibold text-gray-900'>
                      {result?.score ?? '—'}
                    </span>
                  </div>

                  {/* Status */}
                  <div className='flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2'>
                    <span className='text-sm text-gray-600 font-medium'>Status</span>
                    <span
                      className={`text-xs font-semibold px-4 py-1 rounded-full capitalize ${
                        result?.status === 'pass'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}
                    >
                      {result?.status ?? '—'}
                    </span>
                  </div>

                  {/* Completion Status — if available */}
                  {result?.completionStatus && (
                    <div className='flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2'>
                      <span className='text-sm text-gray-600 font-medium'>Completion</span>
                      <span className='text-sm font-semibold text-gray-900 capitalize'>
                        {result.completionStatus}
                      </span>
                    </div>
                  )}

                  {/* Total questions & correct answers — if available */}
                  {result?.totalQuestions != null && (
                    <div className='flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2'>
                      <span className='text-sm text-gray-600 font-medium'>Correct Answers</span>
                      <span className='text-sm font-semibold text-gray-900'>
                        {result.correctAnswers ?? 0} / {result.totalQuestions}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </section>

        {/* Footer actions */}
        <div className='flex justify-between items-center mt-3'>
          <button
            className='btn-light bg-primaryGray px-6'
            onClick={handleClose}
          >
            Close
          </button>

          <div className='flex gap-3'>
            {currentView === 'result' && (
              <button
                className='btn-light bg-primaryGray px-6'
                onClick={() => setCurrentView('preview')}
              >
                <span className='bi bi-arrow-left mr-1'></span> Back to Preview
              </button>
            )}

            {currentView === 'preview' ? (
              <button
                className='btn bg-primary-blue px-6'
                onClick={handleViewResult}
                disabled={!props.previewId}
              >
                Finish &amp; View Result
              </button>
            ) : (
              <button
                className='btn bg-primary-blue px-6'
                onClick={() => navigate('/admin/modules/')}
              >
                Done, View Modules
              </button>
            )}
          </div>
        </div>
      </CenteredModal>
    </>
  )
}

export default PreviewModuleModal